/**
 * The timed pair's pure rules (pedagogy spec §7.3.6, E6): which two problems, how long, and the
 * student's own pace. The fetch and the session are in `startSet.ts`.
 *
 * Two exam-style problems under a countdown of 0.8 × the student's exam pace: 80 minutes after the
 * default five-hour sitting (§13 Q16). The pace is minutes per exam point, so the length follows the
 * points of the two problems actually served (known only after the fetch, which is why the link
 * carries `perPoint`, not minutes).
 */
import {
  DEFAULT_SITTING,
  timedPairMinutes,
  timedPairPerPoint,
  TIMED_PAIR_POINTS,
} from "@shared/examWrapper";
import { EXAM_TOTAL_POINTS } from "@shared/exam";
import type { PublicQuestion } from "@shared/types";

/** The pace when a link carries none and the student has no submitted exam: 13.33 min per point. */
export const DEFAULT_PER_POINT = timedPairPerPoint(DEFAULT_SITTING);

/**
 * The two problems of the pair: the two worth the most points, three-pointers first, served order
 * breaking ties. Fewer than two problems give fewer than two.
 */
export function pickPair(questions: readonly PublicQuestion[]): PublicQuestion[] {
  return questions
    .map((question, order) => ({ question, order }))
    .sort((a, b) => b.question.points - a.question.points || a.order - b.order)
    .slice(0, 2)
    .map(({ question }) => question);
}

/** The countdown in seconds: `timedPairMinutes` of the pair's points at `perPoint`. */
export function limitSecOf(pair: readonly PublicQuestion[], perPoint: number): number {
  const points = pair.reduce((sum, question) => sum + question.points, 0);
  return timedPairMinutes(points > 0 ? points : TIMED_PAIR_POINTS, perPoint) * 60;
}

/** The slice of an exam history entry the pace reads (`GET /api/exam/history`). */
export interface PaceEntry {
  status: string;
  durationMinutes?: number;
}

/**
 * Minutes per point at 0.8 × the pace of the student's most recent submitted exam (history is
 * newest first), or null without one: the sitting's length over the paper's points. Entries
 * from before the server sent `durationMinutes` are passed over.
 */
export function perPointFromHistory(history: readonly PaceEntry[]): number | null {
  for (const entry of history) {
    if (entry.status !== "submitted" && entry.status !== "graded") continue;
    const minutes = entry.durationMinutes;
    if (typeof minutes !== "number" || !Number.isFinite(minutes) || minutes <= 0) continue;
    return timedPairPerPoint({ minutes, paperPoints: EXAM_TOTAL_POINTS });
  }
  return null;
}
